// ConnectionLayer — renders SVG connections between nodes using layout positions.
// Connection styles are resolved from topology data with built-in defaults.

import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectConnections,
  selectConnectionStyles,
  selectFrames,
  selectLanes,
  selectBranchLanes,
} from '../../store/slices/topologySlice.ts';
import {
  selectActiveConnections,
  selectActivatedBranches,
  selectActivatedBranchRecords,
} from '../../store/slices/simulationSlice.ts';
import { selectActivePaths } from '../../store/slices/uiSlice.ts';
import { resolveConnectionStyle } from '../../services/styleResolver.ts';
import { isConnectionVisible } from './connectionHighlight.ts';
import { getCachedEventCatalog } from '../../services/ArtifactLoaderService.ts';
import { useInformationDensity } from './useInformationDensity.ts';
import type { NodePosition } from './layoutEngine.ts';

const RELATIONSHIP_ABBREV: Record<string, string> = {
  'customer-supplier': 'C/S',
  partnership: 'P',
  conformist: 'CF',
  'anticorruption-layer': 'ACL',
  'open-host-service': 'OHS',
  'published-language': 'PL',
  'separate-ways': 'SW',
};

function buildPath(fromPos: NodePosition, toPos: NodePosition): string {
  const x1 = fromPos.x + fromPos.w + 6;
  const y1 = fromPos.y + fromPos.h / 2;
  const x2 = toPos.x + 6;
  const y2 = toPos.y + toPos.h / 2;

  const dx = x2 - x1;
  const dy = y2 - y1;
  const isVertical = Math.abs(dy) > 20;
  const isBackward = dx < 0;

  if (isBackward) {
    const arcY = Math.min(y1, y2) - 40;
    return `M${x1},${y1} C${x1 + 40},${arcY} ${x2 - 40},${arcY} ${x2},${y2}`;
  } else if (isVertical) {
    const cpx = (x1 + x2) / 2;
    return `M${x1},${y1} C${cpx},${y1} ${cpx},${y2} ${x2},${y2}`;
  } else {
    return `M${x1},${y1} L${x2},${y2}`;
  }
}

export interface ConnectionLayerProps {
  nodePositions: Record<string, NodePosition>;
}

export default function ConnectionLayer({ nodePositions }: ConnectionLayerProps) {
  const connections = useSelector(selectConnections);
  const frames = useSelector(selectFrames);
  const activeConnectionIds = useSelector(selectActiveConnections);
  // Structured per-branch-point records (new contract) + legacy route strings
  // (style-based fallback for old-format topologies only).
  const activatedBranchRecords = useSelector(selectActivatedBranchRecords);
  const activatedBranches = useSelector(selectActivatedBranches);
  const activePaths = useSelector(selectActivePaths);
  const topologyStyles = useSelector(selectConnectionStyles);

  const { showConnectionLabels, showRelationshipPills } = useInformationDensity();

  const activeSet = new Set(activeConnectionIds);

  // Build event name → consumer count lookup from event catalog
  const catalog = getCachedEventCatalog();
  const consumerCounts = useMemo(() => {
    if (!catalog) return new Map<string, number>();
    const counts = new Map<string, number>();
    for (const entry of catalog.events) {
      if (entry.consumers.length > 0) {
        counts.set(entry.eventName, entry.consumers.length);
      }
    }
    return counts;
  }, [catalog]);

  // Build extended position map that resolves frame IDs and context IDs
  // to actual node positions (for Moment M6 topology connections).
  // Moment connections use: from=frameId, to=contextId (e.g., ctx-Inventory)
  const lanes = useSelector(selectLanes);
  const branchLanes = useSelector(selectBranchLanes);
  const allLanes = useMemo(() => [...lanes, ...branchLanes], [lanes, branchLanes]);

  const resolvedPositions = useMemo(() => {
    const resolved: Record<string, NodePosition> = { ...nodePositions };

    // Build lane→contextId lookup
    const laneToContext = new Map<string, string>();
    for (const lane of allLanes) {
      if (lane.contextId) laneToContext.set(lane.id, lane.contextId);
    }

    // Map frame IDs → last event node in that frame (the crossing source)
    for (const frame of frames) {
      if (frame.nodes.length > 0 && !resolved[frame.id]) {
        // Prefer the last event-type node (the "output" of the moment)
        const eventNode = [...frame.nodes].reverse().find((n) => n.type === 'event');
        const targetNode = eventNode ?? frame.nodes[frame.nodes.length - 1];
        if (nodePositions[targetNode.id]) {
          resolved[frame.id] = nodePositions[targetNode.id];
        }
      }
    }

    // Map context IDs → best representative node for that context.
    // Two-pass: first pass picks any node, second pass overwrites with command nodes.
    const ctxNodeCandidates = new Map<string, string>(); // ctxId → node.id
    for (const frame of frames) {
      for (const node of frame.nodes) {
        const ctxKey = laneToContext.get(node.lane);
        if (ctxKey && nodePositions[node.id]) {
          if (!ctxNodeCandidates.has(ctxKey)) {
            ctxNodeCandidates.set(ctxKey, node.id);
          } else if (node.type === 'command') {
            // Prefer command nodes (the "input" of a context crossing)
            ctxNodeCandidates.set(ctxKey, node.id);
          }
        }
      }
    }
    for (const [ctxId, nodeId] of ctxNodeCandidates) {
      resolved[ctxId] = nodePositions[nodeId];
    }

    return resolved;
  }, [nodePositions, frames, allLanes]);

  return (
    <g aria-hidden="true">
      {connections.map((conn) => {
        const connId = `${conn.from}->${conn.to}`;
        const isActive = activeSet.has(connId);
        if (
          !isConnectionVisible(
            conn,
            activePaths,
            activatedBranchRecords,
            activatedBranches,
            topologyStyles,
          )
        ) {
          return null;
        }

        const fromPos = resolvedPositions[conn.from];
        const toPos = resolvedPositions[conn.to];
        if (!fromPos || !toPos) return null;

        const cs = resolveConnectionStyle(conn.style, topologyStyles);
        const pathD = buildPath(fromPos, toPos);
        const isCrossBoundary = !!conn.crossBoundary;

        const x1 = fromPos.x + fromPos.w + 6;
        const y1 = fromPos.y + fromPos.h / 2;
        const x2 = toPos.x + 6;
        const y2 = toPos.y + toPos.h / 2;
        const midX = (x1 + x2) / 2;
        const midY = (y1 + y2) / 2;

        const strokeW = isCrossBoundary ? 2 : 1;
        const relAbbrev = conn.relationship
          ? (RELATIONSHIP_ABBREV[conn.relationship] ?? conn.relationship)
          : null;

        return (
          <g key={connId} data-style={conn.style}>
            {/* Glow effect for cross-boundary connections */}
            {isCrossBoundary && (
              <path
                d={pathD}
                fill="none"
                stroke={cs.color}
                strokeWidth={strokeW + 3}
                strokeDasharray={cs.dash ? '5,3' : 'none'}
                opacity={isActive ? 0.15 : 0.08}
              />
            )}
            <path
              d={pathD}
              fill="none"
              stroke={cs.color}
              strokeWidth={strokeW}
              strokeDasharray={cs.dash ? '5,3' : 'none'}
              opacity={isActive ? 0.8 : 0.5}
            />
            {/* Circle endpoint at target */}
            <circle
              cx={x2}
              cy={y2}
              r={isCrossBoundary ? 3 : 2.5}
              fill={cs.color}
              opacity={isActive ? 0.9 : 0.6}
            />
            {/* Connection label — only when active so labels don't orphan */}
            {showConnectionLabels && isActive && conn.label && (
              <text
                x={midX}
                y={Math.min(y1, y2) - 5}
                textAnchor="middle"
                fill={cs.color}
                fontSize="8"
                fontFamily="'JetBrains Mono', monospace"
                opacity={isActive ? 0.9 : 0.7}
              >
                {conn.label}
              </text>
            )}
            {/* Consumer count badge — only on active cross-boundary connections */}
            {showConnectionLabels &&
              isActive &&
              isCrossBoundary &&
              conn.label &&
              (() => {
                const count = consumerCounts.get(conn.label);
                if (!count) return null;
                const badgeX = x2 + 8;
                const badgeY = y2 - 10;
                return (
                  <>
                    <rect
                      x={badgeX - 10}
                      y={badgeY - 6}
                      width={20}
                      height={12}
                      rx={6}
                      fill="var(--bg-pill)"
                      stroke={cs.color}
                      strokeWidth={0.5}
                      strokeOpacity={0.5}
                    />
                    <text
                      x={badgeX}
                      y={badgeY + 1}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fill={cs.color}
                      fontSize="7"
                      fontWeight={600}
                      fontFamily="'JetBrains Mono', monospace"
                      opacity={0.85}
                    >
                      {count}→
                    </text>
                  </>
                );
              })()}
            {/* DDD relationship pill — only when active so pills don't orphan */}
            {showRelationshipPills && isActive && isCrossBoundary && relAbbrev && (
              <>
                <rect
                  x={midX - 12}
                  y={midY - 7}
                  width={24}
                  height={12}
                  rx={3}
                  fill="var(--bg-pill)"
                  stroke={cs.color}
                  strokeWidth={0.5}
                  strokeOpacity={0.4}
                />
                <text
                  x={midX}
                  y={midY + 1}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill={cs.color}
                  fontSize="7"
                  fontWeight={700}
                  fontFamily="'JetBrains Mono', monospace"
                  opacity={isActive ? 0.95 : 0.75}
                >
                  {relAbbrev}
                </text>
              </>
            )}
          </g>
        );
      })}
    </g>
  );
}
